import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import moment from 'moment/min/moment-with-locales';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { useShiftPayment } from '@/query/queries/loan/useShiftPayment';
import Notify from '@/components/Notification';
import { prettyFloatMoney } from '@/helpers/utils';
import { setModals } from '@/store/actions/commonActions';
import { MODAL_TYPES } from '@/components/ModalProvider';
import { Preloader } from '@/components/Preloader';
import { PaymentSelect } from './components/PaymentSelect';
import IconAnnotate from './icon_annotation.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const id = MODAL_IDS.shift_payment_modal;

export const ShiftPaymentModal = () => {
  const { modalIds, data } = useModal();
  return modalIds[id] && data && <Modal data={data} />;
};

const Modal = ({ data }) => {
  const { payments, fundraisingId, feeAmount } = data;
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('modal');
  const { mutateAsync: shiftPayment } = useShiftPayment(fundraisingId);
  const { closeModal } = useModal();

  useEffect(() => {
    if (!selected && payments.length) {
      const selected = payments.find(payment => payment.selected);
      setSelected(selected);
    }
  }, [selected, payments]);

  const onConfirm = async () => {
    try {
      setLoading(true);
      const payload = {
        original_deadline: selected.original_deadline,
      };
      const res = await shiftPayment(payload);
      if (res?.success) {
        Notify.success({ text: t('shift_payment_modal.payment_moved_successfully') });
        data?.loan &&
          dispatch(
            setModals({
              type: MODAL_TYPES.loan_info,
              selectedLoan: data.loan,
            }),
          );
        closeModal(id);
      }
    } catch {
      Notify.error({ text: 'Something went wrong' });
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    data?.loan &&
      dispatch(
        setModals({
          type: MODAL_TYPES.loan_info,
          selectedLoan: data.loan,
        }),
      );
    closeModal(id);
  };

  if (!selected) return null;

  const content = (
    <div className={classnames(s.modal, 'scr_modal_grid')}>
      <div className="f-h2 f-500">{t('shift_payment_modal.move_payment')}</div>
      <div className={s.content}>
        <PaymentSelect
          options={payments}
          selected={selected}
          onChange={payment => setSelected(payment)}
        />
        <div className={s.annotation}>
          <img
            src={IconAnnotate}
            alt="annotate"
            width={20}
            height={20}
            style={{ minWidth: '20px' }}
          />
          <span>{t('shift_payment_modal.payment_will_be_moved')}</span>
        </div>
        <div className={s.summary}>
          <div className={s.row}>
            <div className="f-body-regular">{t('shift_payment_modal.deferral_fee')}</div>
            <div className="f-body-medium">{prettyFloatMoney(feeAmount)}</div>
          </div>
          <div className={s.row}>
            <div className="f-body-regular">{t('shift_payment_modal.available_until')}</div>
            <div className="f-body-medium">
              {moment(selected.deadline).subtract('1', 'day').format('D MMMM')} 23:59 (EET)
            </div>
          </div>
        </div>
      </div>
      <div className={s.footer}>
        <button type="button" className={classnames(r.grey_large, s.btn_cancel)} onClick={onCancel}>
          {t('shift_payment_modal.cancel')}
        </button>
        <button
          type="button"
          className={classnames(r.black_large, s.btn_confirm)}
          disabled={loading}
          onClick={onConfirm}
        >
          {loading ? (
            <Preloader style={{ width: '40%' }} />
          ) : (
            t('shift_payment_modal.confirm_and_pay_fee')
          )}
        </button>
      </div>
    </div>
  );

  return (
    <ModalCenterLayout id={id} onClose={onCancel} showClose>
      {content}
    </ModalCenterLayout>
  );
};
